// src/components/trial/lp/trial-beta/sections/hero.tsx
// Ported from the design attachment, markup lines 2397-2532. The section renders the
// STATIC (no-JS) state verbatim — the animated HUD (odometer ping number, live chart
// redraw, phase/callout strips) is layered on top imperatively by `../animation/hero`,
// exactly like the source's own anim-p1.js did to this same markup. See that module's
// header comment and docs/trial-system.md for the split rationale.
import { useTranslation } from 'next-i18next';
import { useState } from 'react';

import { AlreadyLoggedInNotice } from '@/components/trial/already-logged-in-notice';
import { BetaDownloadButton } from '@/components/trial/lp/trial-beta/components/beta-download-button';
import { CertifiedBy } from '@/components/trial/lp/trial-beta/components/certified-by';
import { SignupForm } from '@/components/trial/lp/trial-beta/components/signup-form';
import type { DefaultTrialProps } from '@/components/trial/trial-layout-switcher';
import { useAuth } from '@/hooks/use-auth';

import { heroAnimation } from '../animation/hero';
import { buildHeroHudStrings } from '../animation/hud-i18n';
import { useSectionAnimation } from '../animation/use-section-animation';

interface HeroProps extends DefaultTrialProps {}

export function Hero({ daysTrial, trialConfigId, trialPageId, isDisabledButton }: HeroProps) {
  // Post-signup state lives here rather than in SignupForm: the welcome replaces the whole
  // form card, which the form does not own.
  const [hasRegistered, setHasRegistered] = useState(false);
  const auth = useAuth();
  const { t } = useTranslation('components/trial/lp/trial-beta');
  const sectionRef = useSectionAnimation(heroAnimation, buildHeroHudStrings(t));

  // Started from this page's own notice rather than a signup: the account had no trial, the visitor
  // was eligible and took it, so it earns the same post-signup screen the signup paths get.
  const handleTrialStartedHere = () => {
    setHasRegistered(true);
  };

  // What the card shows is derived from auth state alone, so it reads the same on arrival and after a refresh.
  //
  // A trial the account already held and one it just received render the same screen, so there is
  // no need to tell them apart — which matters, because the front cannot: the SSO endpoint returns
  // only a token, /user/me carries no trial_page_id, and trial-eligibility answers
  // `already_has_trial` for both. The copy holds either way ("your account is ready, download
  // NoPing"), and for a trial that has not started, downloading is exactly the next step.
  //
  // `hasRegistered` covers the moment before auth catches up: the e-mail form and the notice both
  // report success before /user/me has been re-read.
  const showWelcome = hasRegistered || (!auth.loading && Boolean(auth.user?.customTrial));

  // Signed in is the whole condition, exactly as the six other trial LPs have had it since
  // SCRUM-1663 — an authenticated visitor must never be shown a create-account form.
  // AlreadyLoggedInNotice is what names their real state: it offers to start this trial when
  // eligible, and otherwise gives the reason (active_subscription, active_vip).
  const showAlreadyLoggedInNotice = !showWelcome && !auth.loading && Boolean(auth.user);

  // zIndex 3, not 1 (SCRUM-1883). The stat pills hang 24px below the form card by design
  // (`bottom: -24px` in trial-beta.css), so they reach into the next section's box. Every section
  // carries `isolation: isolate`, which makes this one a stacking context — the pills' own
  // z-index competes only inside it and cannot lift them out of it. At 1 this section tied with
  // #multiconnection, and a tie is broken by DOM order, so the later section painted over the
  // pills' bottom third, in every locale. 3 clears it and #how-it-works (z-index 2); the sections
  // never overlap geometrically, so nothing else changes.
  return (
    <section id='hero' ref={sectionRef} className='relative flex min-h-screen items-center px-4 pt-20 md:px-8' style={{ zIndex: 3 }}>
      <div className='pointer-events-none absolute inset-0 overflow-hidden' aria-hidden='true'>
        <div className='absolute start-1/4 top-1/4 size-96 rounded-full blur-[120px]' style={{ background: '#2ECDFF18' }} />
        <div className='absolute bottom-1/4 end-1/4 size-80 rounded-full blur-[100px]' style={{ background: '#93FF1812' }} />
        <div className='absolute inset-0 opacity-[0.03]' style={{ backgroundImage: 'radial-gradient(#2ECDFF 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
      </div>
      <div className='relative mx-auto grid w-full max-w-7xl grid-cols-1 items-stretch gap-8 lg:grid-cols-2 lg:gap-12' style={{ zIndex: 1 }}>
        <div>
          <div data-anim='hero-kicker' className='mb-6 inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-xs font-semibold uppercase tracking-wider' style={{ background: '#93FF1815', border: '1px solid #93FF1830', color: '#93FF18', fontFamily: 'var(--font-jetbrains)' }}>
            <span className='size-1.5 animate-pulse rounded-full bg-[#93FF18]' />
            {t('hero.kicker')}
          </div>
          <h1 data-anim='hero-h1' className='mb-4 text-4xl font-bold leading-tight md:text-5xl lg:text-6xl' style={{ fontFamily: 'var(--font-bai)', color: '#E5E7EB' }}>
            {t('hero.title')} <span className='bg-gradient-to-r from-[#2ECDFF] to-[#2ECDFF]/80 bg-clip-text text-transparent'>{t('hero.titleHighlight')}</span>
          </h1>
          <p data-anim='hero-sub' className='mb-6 max-w-lg text-lg leading-relaxed' style={{ color: '#929298', fontFamily: 'var(--font-outfit)' }}>
            {t('hero.description')}
          </p>
          {/* Beta installer CTA above the fold: a visitor who already has an account should not
              have to pass through the form to get the build. */}
          <div className='mb-6'>
            <BetaDownloadButton tone='hero' />
          </div>
          <div className='mb-6'>
            <div data-anim='hero-ping-number' className='flex flex-col items-center gap-1'>
              <div className='font-mono text-7xl font-black tabular-nums' style={{ color: '#FD3154' }}>
                168<span className='text-[0.4em] font-normal opacity-60'>ms</span>
              </div>
              <span className='text-xs uppercase tracking-[0.2em] text-[#929298]'>{t('hero.pingLabel')}</span>
            </div>
          </div>
          <div className='relative overflow-hidden rounded-xl border' style={{ borderColor: '#93FF1830' }}>
            {/* static stand-in for the app client video (pc-client-en.mp4): live ping line chart */}
            <svg data-anim='hero-chart' data-testid='hero-chart' viewBox='0 0 800 450' className='h-auto w-full' preserveAspectRatio='xMidYMid meet' aria-hidden='true'>
              <rect x='0' y='0' width='800' height='450' fill='#0D0D12' />
              <g stroke='#FFFFFF' strokeOpacity='0.05' strokeDasharray='4 4'>
                <line x1='0' y1='100' x2='800' y2='100' />
                <line x1='0' y1='180' x2='800' y2='180' />
                <line x1='0' y1='260' x2='800' y2='260' />
                <line x1='0' y1='340' x2='800' y2='340' />
              </g>
              <line x1='40' y1='400' x2='760' y2='400' stroke='#FFFFFF' strokeOpacity='0.15' strokeWidth='1' />
              <polyline fill='none' stroke='#2ECDFF' strokeOpacity='0.85' strokeWidth='2.5' strokeLinejoin='round' points='40,120 76,96 112,150 148,88 184,132 220,102 256,164 292,110 328,140 364,118' />
              <polyline fill='none' stroke='#93FF18' strokeWidth='2.5' strokeLinejoin='round' points='364,118 400,190 436,260 472,318 508,356 544,376 580,384 616,386 652,385 688,386 724,385 760,386' />
              <polygon fill='#93FF1815' stroke='none' points='364,118 400,190 436,260 472,318 508,356 544,376 580,384 616,386 652,385 688,386 724,385 760,386 760,400 364,400' />
              <circle cx='760' cy='386' r='5' fill='#93FF18' />
              <circle cx='760' cy='386' r='10' fill='#93FF18' fillOpacity='0.2' />
            </svg>
          </div>
        </div>
        <div id='hero-form' className='relative flex flex-col pb-6'>
          <div data-anim='hero-form-card' className='relative flex flex-1 flex-col justify-center rounded-xl border p-6 md:p-8' style={{ backgroundColor: '#0D0D12F0', borderColor: '#93FF1830', boxShadow: '0 0 30px #93FF1810, inset 0 1px 0 #93FF1815' }}>
            <div className='pointer-events-none absolute inset-0' style={{ background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(46,205,255,0.02) 2px, rgba(46,205,255,0.02) 4px)' }} aria-hidden='true' />
            <div className='relative z-10 flex flex-1 flex-col'>
              {/* The notice brings its own heading and its own explanation of the account's state,
                  so the card's "create your account" pair would contradict it right above. */}
              {!showAlreadyLoggedInNotice && (
                <>
                  <h2 className='mb-2 text-center text-2xl font-bold md:text-3xl' style={{ fontFamily: 'var(--font-bai)', color: '#E5E7EB' }}>
                    {showWelcome ? t('betaDownload.welcomeTitle') : t('hero.formTitle')}
                  </h2>
                  <p className='mb-5 text-center text-sm' style={{ color: '#929298', fontFamily: 'var(--font-outfit)' }}>
                    {showWelcome ? t('betaDownload.welcomeSubtitle') : t('hero.subtitle', { count: daysTrial })}
                  </p>
                </>
              )}
              {/* The source's `<form id="v2b-hero-form">` (SSO buttons, fields, reCAPTCHA mock,
                  submit) is SignupForm — the conversion-critical unit built in Task 4. `formId`
                  puts the id on the actual <form> (not a wrapper div) so it sits at the same DOM
                  depth as the source: trial-beta.css has eight `#v2b-hero-form > ...` direct-child
                  rules (1024px breakpoint) that only resolve at that exact depth. Same pattern as
                  `CreateAccountV2`'s `formId` prop, used the same way by `lp/v2b/index.tsx`. */}
              {showWelcome && (
                <div className='flex flex-col items-center gap-3 py-4'>
                  <BetaDownloadButton tone='welcome' />
                </div>
              )}
              {showAlreadyLoggedInNotice && <AlreadyLoggedInNotice trialConfigId={trialConfigId} trialPageId={trialPageId} onTrialStarted={handleTrialStartedHere} />}
              {!showWelcome && !showAlreadyLoggedInNotice && <SignupForm daysTrial={daysTrial} trialConfigId={trialConfigId} trialPageId={trialPageId} isDisabledButton={isDisabledButton} formId='v2b-hero-form' onSuccess={() => setHasRegistered(true)} />}
              <CertifiedBy />
            </div>
          </div>
          <div data-anim='hero-stat-pills' className='mt-4 flex flex-wrap justify-center gap-2 px-4 md:absolute md:inset-x-0 md:-bottom-0 md:z-20 md:mt-0 md:gap-3'>
            <div className='flex items-center gap-1.5 rounded-full border px-3 py-1.5 shadow-lg' style={{ backgroundColor: '#0c0e14', borderColor: '#93FF1840', boxShadow: '0 0 12px #93FF1815' }}>
              <span className='text-xs font-bold' style={{ color: '#93FF18', fontFamily: 'var(--font-bai)' }}>
                80%
              </span>
              <span className='text-[9px] uppercase tracking-wider' style={{ color: '#929298', fontFamily: 'var(--font-outfit)' }}>
                {t('hero.statPing')}
              </span>
            </div>
            <div className='flex items-center gap-1.5 rounded-full border px-3 py-1.5 shadow-lg' style={{ backgroundColor: '#0c0e14', borderColor: '#93FF1840', boxShadow: '0 0 12px #93FF1815' }}>
              <span className='text-xs font-bold' style={{ color: '#93FF18', fontFamily: 'var(--font-bai)' }}>
                110%
              </span>
              <span className='text-[9px] uppercase tracking-wider' style={{ color: '#929298', fontFamily: 'var(--font-outfit)' }}>
                {t('hero.statFps')}
              </span>
            </div>
            <div className='flex items-center gap-1.5 rounded-full border px-3 py-1.5 shadow-lg' style={{ backgroundColor: '#0c0e14', borderColor: '#93FF1840', boxShadow: '0 0 12px #93FF1815' }}>
              <span className='text-xs font-bold' style={{ color: '#93FF18', fontFamily: 'var(--font-bai)' }}>
                6+
              </span>
              <span className='text-[9px] uppercase tracking-wider' style={{ color: '#929298', fontFamily: 'var(--font-outfit)' }}>
                {t('hero.statMultiNet')}
              </span>
            </div>
            <div className='flex items-center gap-1.5 rounded-full border px-3 py-1.5 shadow-lg' style={{ backgroundColor: '#0c0e14', borderColor: '#93FF1840', boxShadow: '0 0 12px #93FF1815' }}>
              <span className='text-xs font-bold' style={{ color: '#93FF18', fontFamily: 'var(--font-bai)' }}>
                5+
              </span>
              <span className='text-[9px] uppercase tracking-wider' style={{ color: '#929298', fontFamily: 'var(--font-outfit)' }}>
                {t('hero.statMulticonn')}
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
